import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetList, useUpdate, useUpdateMany } from "ra-core";
import { Link } from "react-router";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Notification = {
  id: number;
  type: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: number | null;
  read_at: string | null;
  created_at: string;
};

/** Where a notification takes you. Unknown types stay inert rather than guess. */
const linkFor = (n: Notification) => {
  switch (n.entity_type) {
    case "ticket":
      return `/tickets/${n.entity_id}/show`;
    case "project":
      return `/projects/${n.entity_id}/show`;
    default:
      return null;
  }
};

const ago = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * The bell, and what is behind it.
 *
 * Rows are written by database triggers and scoped by RLS to their recipient,
 * so this reads its own inbox with no filtering of its own and cannot be shown
 * someone else's.
 */
export function NotificationsMenu() {
  const { data, isPending, refetch } = useGetList<Notification>(
    "notifications",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "created_at", order: "DESC" },
    },
    // A slow fallback only. The live path is the Realtime subscription below;
    // this covers a dropped socket, which is quiet rather than loud.
    { refetchInterval: 300_000 },
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    // Notifications are written by database triggers, so nothing this tab does
    // invalidates the cache. Realtime is what makes the bell arrive rather than
    // wait for a poll. RLS applies to the subscription, so only the recipient's
    // own rows are delivered.
    const channel = getSupabaseClient()
      .channel("own-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
      )
      .subscribe();

    return () => {
      getSupabaseClient().removeChannel(channel);
    };
  }, [queryClient]);

  const [update] = useUpdate();
  const [updateMany] = useUpdateMany();

  if (isPending) return null;

  const notifications = data ?? [];
  const unread = notifications.filter((n) => n.read_at == null);

  const markRead = (n: Notification) => {
    if (n.read_at) return;
    update(
      "notifications",
      {
        id: n.id,
        data: { read_at: new Date().toISOString() },
        previousData: n,
      },
      { onSuccess: () => refetch() },
    );
  };

  const markAllRead = () =>
    updateMany(
      "notifications",
      {
        ids: unread.map((n) => n.id),
        data: { read_at: new Date().toISOString() },
      },
      { onSuccess: () => refetch() },
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="size-5" />
          {unread.length > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.6rem] font-medium text-primary-foreground">
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          {unread.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={markAllRead}>
              Mark all read
            </Button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Nothing yet.
            </p>
          ) : (
            notifications.map((n) => {
              const to = linkFor(n);
              const body = (
                <div
                  className={
                    n.read_at
                      ? "px-3 py-2 hover:bg-accent/50"
                      : "px-3 py-2 hover:bg-accent/50 bg-accent/30"
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium">{n.title}</span>
                    {n.read_at ? null : (
                      <Badge variant="secondary" className="shrink-0">
                        new
                      </Badge>
                    )}
                  </div>
                  {n.body ? (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {n.body}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground pt-0.5">
                    {ago(n.created_at)}
                  </p>
                </div>
              );

              return to ? (
                <Link key={n.id} to={to} onClick={() => markRead(n)}>
                  {body}
                </Link>
              ) : (
                <div key={n.id} onClick={() => markRead(n)}>
                  {body}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
